import React, { lazy, Suspense } from 'react';
import { createRoutesFromElements, Outlet, Route } from 'react-router-dom';
import { Layout } from '@flexible-reports/components/layout';
import { ErrorBoundary } from '@flexible-reports/components/error-boundary/error-boundary';
import {
  isFlexibleReportsM2P1Enabled,
  isCaracalOnlineExperienceEnabled,
  isUnifiedReportingEnabled,
  isCaracalReportAnalysisEnabled
} from '@flexible-reports/utils/app-utils';

import(/* webpackChunkName: "InitUDC" */ '@urc-ui/services/init-udc');
const LazyAnalysis = lazy(() => import(/* webpackChunkName: "FlexibleReportsAnalysis" */ '@flexible-reports/pages/analysis'));
const LazyLanding = lazy(() => import(/* webpackChunkName: "FlexibleReportsLanding" */ '@flexible-reports/pages/landing'));
const LazyCreateSubscription = lazy(() =>
  import(/* webpackChunkName: "FlexibleReportsCreateNewReport" */ '@flexible-reports/pages/create-subscription').then(
    ({ CreateSubscription }) => ({
      default: CreateSubscription
    })
  )
);
const LazyOnlineReportBuilder = lazy(() =>
  import(/* webpackChunkName: "FlexibleReportsCreateNewReport" */ '@flexible-reports/pages/online-report-builder').then(
    ({ OnlineReportBuilder }) => ({
      default: OnlineReportBuilder
    })
  )
);

const LazyViewSubscriptionLayout = lazy(
  () => import(/* webpackChunkName: "ViewSubscriptionLayout" */ '@flexible-reports/pages/view-subscription/layout')
);

const LazyCaracalReportHistoryLayout = lazy(() =>
  import(/* webpackChunkName: "CaracalReportHistoryLayout" */ '@flexible-reports/pages/view-subscription/layout').then(
    ({ CaracalReportHistoryLayout }) => ({
      default: CaracalReportHistoryLayout
    })
  )
);

const LazyReportHistory = lazy(
  () => import(/* webpackChunkName: "FlexibleReportsHistory" */ '@flexible-reports/pages/view-subscription/report-history')
);

const LazyReportSettings = lazy(
  () => import(/* webpackChunkName: "FlexibleReportsSettings" */ '@flexible-reports/pages/view-subscription/report-settings')
);

const LazyCustomRouteValidator = lazy(() => import(/* webpackChunkName: "CustomRouteValidator" */ '@urc-ui/pages/custom-route-validator'));

const CustomRouteValidator = () => (
  <Suspense fallback={<>...</>}>
    <LazyCustomRouteValidator />
  </Suspense>
);

const Landing = () => (
  <Suspense fallback={<>...</>}>
    <LazyLanding />
  </Suspense>
);

const OnlineReportBuilder = () => (
  <Suspense fallback={<>...</>}>
    <LazyOnlineReportBuilder />
  </Suspense>
);

const CreateSubscription = () => (
  <Suspense fallback={<>...</>}>
    <LazyCreateSubscription />
  </Suspense>
);

const ViewSubscriptionLayout = () => (
  <Suspense fallback={<>...</>}>
    <LazyViewSubscriptionLayout />
  </Suspense>
);

const CaracalReportHistoryLayout = () => (
  <Suspense fallback={<>...</>}>
    <LazyCaracalReportHistoryLayout />
  </Suspense>
);

const ReportHistory = () => (
  <Suspense fallback={<>...</>}>
    <LazyReportHistory />
  </Suspense>
);

const ReportSettings = () => (
  <Suspense fallback={<>...</>}>
    <LazyReportSettings />
  </Suspense>
);

const Analysis = () => (
  <Suspense fallback={<>...</>}>
    <LazyAnalysis />
  </Suspense>
);

const CaracalP0Routes = () => {
  return (
    <>
      <Route path='/' element={<Layout hasSideNav={false} withMaxWidthContainer={true} />}>
        <Route path='/new' element={<OnlineReportBuilder />} />
        <Route path='/copy' element={<OnlineReportBuilder />} />
        <Route path='/edit' element={<OnlineReportBuilder />} />
        {isCaracalReportAnalysisEnabled() && <Route path='/analysis' element={<Analysis />} />}
      </Route>
      <Route path='/' element={<Layout hasSideNav withMaxWidthContainer={true} withoutPadding={true} />}>
        <Route element={<CaracalReportHistoryLayout />}>
          <Route path='/history' element={<ReportHistory />} />
        </Route>
      </Route>
    </>
  );
};

const CaracalPreP0Routes = () => {
  const isUnifiedReporting = isUnifiedReportingEnabled();
  return (
    <>
      <Route path='/' element={<Layout hasSideNav={false} />}>
        <Route path='/new' element={<CreateSubscription />} />
        {isFlexibleReportsM2P1Enabled() && <Route path='/copy' element={<CreateSubscription />} />}
      </Route>
      {isCaracalReportAnalysisEnabled() && (
        <Route path='/' element={<Layout hasSideNav={true} withMaxWidthContainer={true} />}>
          {isCaracalReportAnalysisEnabled() && <Route path='/analysis' element={<Analysis />} />}
        </Route>
      )}
      <Route path='/' element={<Layout hasSideNav withMaxWidthContainer={isUnifiedReporting} withoutPadding={isUnifiedReporting} />}>
        <Route element={<ViewSubscriptionLayout />}>
          <Route path='/history' element={<ReportHistory />} />
          <Route path='/settings' element={<ReportSettings />} />
        </Route>
      </Route>
    </>
  );
};

const FlexibleReportsRoutes = () => {
  return createRoutesFromElements(
    <Route element={<Outlet />} ErrorBoundary={ErrorBoundary}>
      <Route path='/' element={<Layout hasSideNav isFluid withMaxWidthContainer={true} withoutPadding={true} />}>
        <Route index element={<Landing />} />
      </Route>
      {isCaracalOnlineExperienceEnabled() ? CaracalP0Routes() : CaracalPreP0Routes()}
      <Route path='*' element={<CustomRouteValidator />} />
    </Route>
  );
};

export { FlexibleReportsRoutes };
